import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronRight, Plus, Trash2, Loader2 } from "lucide-react";

const MODE_LABELS = { indicator: "Indicator", strategy: "Strategy", alert: "Alert", debug: "Debug", improve: "Improve" };

export default function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    api.get("/scripts").then(({ data }) => { setScripts(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleDelete = async (script, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (deletingId) return;
    const ok = window.confirm(
      `Möchtest du dieses Skript permanent löschen?\n\n"${script.title}"\n\nDiese Aktion kann nicht rückgängig gemacht werden.`
    );
    if (!ok) return;
    setDeletingId(script.id);
    // Optimistic UI — drop the row instantly. Rollback on server failure.
    const snapshot = scripts;
    setScripts((prev) => prev.filter((s) => s.id !== script.id));
    try {
      await api.delete(`/scripts/${script.id}`);
      toast.success("Script deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setScripts(snapshot);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = filter === "all" ? scripts : scripts.filter(s => s.mode === filter);

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-xs font-mono text-blue-400 mb-2">/ HISTORY</p>
          <h1 className="text-4xl font-semibold tracking-tighter bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">Script history</h1>
        </div>
        <Link to="/generate" data-testid="scripts-new-btn"><Button className="bg-blue-600 hover:bg-blue-500 text-white"><Plus className="w-4 h-4 mr-2" />New script</Button></Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "indicator", "strategy", "alert", "debug", "improve"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider border transition-colors ${
              filter === f ? "bg-blue-500/10 border-blue-500/40 text-blue-400" : "bg-[#09090B]/90 border-[#1F2937] text-zinc-500 hover:border-[#2D313F] hover:text-zinc-300"
            }`}
          >
            {f === "all" ? "All" : MODE_LABELS[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 py-16 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-12 text-center">
          <p className="text-zinc-500 mb-4">No scripts in this filter.</p>
          <Link to="/generate"><Button className="bg-blue-600 hover:bg-blue-500 text-white">Generate your first</Button></Link>
        </div>
      ) : (
        <div className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl divide-y divide-[#1F2937]" data-testid="scripts-list">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`script-row-${s.id}`}
                 className="flex items-center justify-between px-6 py-4 hover:bg-[#12141C]/60 transition-colors">
              <Link to={`/scripts/${s.id}`} className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{MODE_LABELS[s.mode] || s.mode}</span>
                  <span className="text-xs text-zinc-500 font-mono">{new Date(s.created_at).toLocaleString()}</span>
                </div>
                <p className="text-white truncate">{s.title}</p>
              </Link>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => handleDelete(s, e)}
                  disabled={deletingId === s.id}
                  data-testid="delete-script-btn"
                  aria-label={`Delete ${s.title}`}
                  title="Delete script"
                  className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deletingId === s.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </button>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
